import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import {
  UserProfile,
  UserProfileUpdate,
  getUserProfile,
  updateUserProfile,
} from '../services/user.service';

export const userProfileKeys = {
  me: ['user', 'me'] as const,
};

export const useUserProfile = (options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();

  const query = useQuery<UserProfile, Error>({
    queryKey: userProfileKeys.me,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await getUserProfile(token);
      if (!response.data) throw new Error(response.error || 'Failed to load profile');
      return response.data;
    },
    enabled: isAuthenticated && (options?.enabled ?? true),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const updateMutation = useMutation<UserProfile, Error, UserProfileUpdate>({
    mutationFn: async (data) => {
      const token = await getAccessTokenSilently();
      const response = await updateUserProfile(token, data);
      if (!response.data) throw new Error(response.error || 'Failed to update profile');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userProfileKeys.me });
    },
  });

  return { ...query, updateMutation };
};
